import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Row, Col, Input, InputGroup, Spinner } from "reactstrap";
import Multiselect from "multiselect-react-dropdown";
import { CheckUserExistence } from "../../../slices/thunks"; // Adjust path as needed

const AddUserForm = ({ labels, formikProps }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const { roles, branches } = useSelector((state) => state.Users);
  const { genders, country: countriesList } = useSelector((state) => state.Module); 
  const { userTypesList } = useSelector((state) => state.UserType); 
  const { courses } = useSelector((state) => state.Courses);
  
  // Destructure from formikProps for easier access
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formikProps;

  // State for email and mobile existence check
  const [emailCheckStatus, setEmailCheckStatus] = useState({ loading: false, available: null, checkedValue: '', error: null });
  const [mobileCheckStatus, setMobileCheckStatus] = useState({ loading: false, available: null, checkedValue: '', error: null });

  const handleExistenceCheck = async (fieldName, value) => {
    let setCheckStatus, payload;

    if (fieldName === "Email") {
      if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || value === emailCheckStatus.checkedValue) return;
      setCheckStatus = setEmailCheckStatus;
      payload = { email: value };
    } else if (fieldName === "Phone") {
      if (!value) return;
      setCheckStatus = setMobileCheckStatus;
      payload = { mobile: value };
    } else {
      return;
    }

    setCheckStatus({ loading: true, available: null, checkedValue: value, error: null });
    try {
      const result = await dispatch(CheckUserExistence(payload)).unwrap();
      if (result.exists) {
        setCheckStatus({
          loading: false,
          available: false, 
          checkedValue: value,
          error: t(result.message) || t(`${fieldName}AlreadyInUse`) 
        });
      } else {
        // Item does not exist, so it's available
        setCheckStatus({ loading: false, available: true, checkedValue: value, error: null });
      }
    } catch (rejectedValueOrSerializedError) {
      // Handle errors from the API call itself (e.g., network error, or thunk rejected)
      const errorMessage = rejectedValueOrSerializedError?.error ||
                           rejectedValueOrSerializedError?.message ||
                           t('existenceCheckFailed');
      if (errorMessage === "Request failed with status code 401") {
        setCheckStatus({ loading: false, available: null, checkedValue: value, error: null });
      } else {
        setCheckStatus({ loading: false, available: null, checkedValue: value, error: errorMessage });
      }
    }
  };

  const renderField = (item) => {
    const fieldValue = values[item.name] ?? "";

    switch (item.fieldName) {
      case "text-box":
      case "email-box":
        const isEmail = item.fieldName === "email-box";
        const currentCheckStatus = isEmail ? emailCheckStatus : null;

        return (
          <InputGroup>
            <Input
              type={"text"}
              inputMode={isEmail ? "email" : "text"} 
              id={item.name}
              name={item.name}
              placeholder={t(item.labelName)}
              value={ fieldValue}
              onChange={(e) => {
                const inputValue = e.target.value;
                
                // If it's RoleName field, allow only letters and spaces
                if (item.name === "FirstName" || item.name === "LastName" ) {
                  const cleanedValue = inputValue.replace(/[^A-Za-z\s]/g, '');
                  setFieldValue(item.name, cleanedValue);
                } else {
                  handleChange(e);
                }
                }}
              maxLength={item.maxLength} 
              minLength={item.minLength}         
              onBlur={(e) => {
                handleBlur(e); // Formik's blur
                if (isEmail) {
                  handleExistenceCheck(item.name, e.target.value);
                }
              }}
              className="form-control"
              invalid={touched[item.name] && !!errors[item.name] || (isEmail && currentCheckStatus?.available === false && currentCheckStatus?.checkedValue === fieldValue)}
              required={item.required}
            />

           {isEmail && (
              <div className="input-group-append" style={{
                display: 'flex',
                alignItems: 'center',
                [i18n.dir() === 'rtl' ? 'marginRight' : 'marginLeft']: '5px',
                minWidth: '25px'
              }}>
                {currentCheckStatus?.loading && <Spinner size="sm" color="primary" />}
                {!currentCheckStatus?.loading && currentCheckStatus?.available === true && currentCheckStatus?.checkedValue === fieldValue && !errors[item.name] && (
                  <span style={{ color: 'green', fontSize: '1.2em' }}>✓</span>
                )}
                {!currentCheckStatus?.loading && currentCheckStatus?.available === false && currentCheckStatus?.checkedValue === fieldValue && !errors[item.name] && (
                  <span style={{ color: 'red', fontSize: '1.2em' }}>✗</span>
                )}
              </div>
            )}
          </InputGroup>
        );

      case "number":
        // This case seems to be for a generic number.
        // If this 'number' type is for mobile, it should ideally use CountryCodeID from formik values
        const countryForNumberField = values.CountryCodeID ? countriesList.find((c) => c.ID === parseInt(values.CountryCodeID)) : null;
        const maxLength = countryForNumberField?.NumbersLimit || 15;

        return (
          <Input
            type="tel"
            id={item.name}
            name={item.name}
            placeholder={t(item.placeholder || item.labelName)}
            value={fieldValue}
            onChange={handleChange} 
            onBlur={handleBlur}
            className="form-control"
            maxLength={maxLength}
            invalid={touched[item.name] && !!errors[item.name]}
            required={item.required}
          />
        );

      case "mobile-box":
          const selectedCountryForMobile = countriesList.find((c) => c.ID === parseInt(values.CountryCodeID));
          const mobileMaxLength = selectedCountryForMobile?.NumbersLimit ?? 10;
          const mobileInputName = item.name; 
          const mobileCurrentCheckStatus = mobileCheckStatus;
          const mobileInputValue = values[mobileInputName] ?? "";
          
          // Render Country Code Select and its error separately
          return (
            <Row>
              <Col sm={4}>
                  <label htmlFor="CountryCodeID" className="col-form-label">
                    {t("Country Code")}
                    {item.required && <span className="text-danger"> *</span>} {/* Assuming country code is required if mobile is */}
                  </label>
                  <Input
                    type="select"
                    id="CountryCodeID"
                    name="CountryCodeID"
                    value={values.CountryCodeID || ""}
                    onChange={(e) => {
                      handleChange(e); // First, handle the change for the country code dropdown
                      setFieldValue(mobileInputName, ""); // Then, clear the mobile number input field
                      // Also, reset the mobile check status
                      setMobileCheckStatus({ loading: false, available: null, checkedValue: '', error: null });
                    }}
                    onBlur={handleBlur}
                    className="form-control"
                    invalid={touched.CountryCodeID && !!errors.CountryCodeID} 
                  >
                    <option value="">{t("Select Country")}</option>
                  {countriesList.map((c) => (
                    // <option key={c.ID} value={c.ID}>
                    //   {c.code} {i18n.language === "en" ? c.en : c.ar}
                    // </option>
                    <option key={c.ID} value={c.ID}>
                      {`\u200E${c.code}`} {i18n.language === "en" ? c.en : c.ar}
                    </option>
                  ))}
                  </Input>
                {/* Display error specifically for CountryCodeID */}
                {touched.CountryCodeID && errors.CountryCodeID && (
                  <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{t(errors.CountryCodeID)}</div>
                )}
              </Col>
              {/* Render Mobile Number Input and its error */}
              <Col sm={8}>
                <label htmlFor="CountryCodeID" className="col-form-label">
                  {t(item.labelName)}
                  {item.required && <span className="text-danger"> *</span>} {/* Assuming country code is required if mobile is */}
                </label>
                <InputGroup>
                  <Input
                    type="text" // Changed to text to allow manual non-numeric input if needed, validation handles numeric
                    id={mobileInputName}
                    name={mobileInputName}
                    placeholder={t(item.placeholder || item.labelName)}
                    value={mobileInputValue} 
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/\D/g, ""); // Allow only digits
                      setFieldValue(mobileInputName, numericValue); 
                    }}
                    onBlur={(e) => {
                      handleBlur(e); // Formik's blur
                      handleExistenceCheck(mobileInputName, e.target.value);
                    }}
                    maxLength={mobileMaxLength}
                    className="form-control"
                    invalid={touched[mobileInputName] && !!errors[mobileInputName] || (mobileCurrentCheckStatus?.available === false && mobileCurrentCheckStatus?.checkedValue === mobileInputValue)}
                    required={item.required}
                  />
                  <div className="input-group-append me-1" style={{ display: 'flex', alignItems: 'center', marginLeft: '5px', minWidth: '25px' }}>
                    {mobileCurrentCheckStatus?.loading && <Spinner size="sm" color="primary" />}
                    {!mobileCurrentCheckStatus?.loading && mobileCurrentCheckStatus?.available === true && mobileCurrentCheckStatus?.checkedValue === mobileInputValue && !errors[mobileInputName] && (
                      <span style={{ color: 'green', fontSize: '1.2em' }}>✓</span>
                    )}
                    {!mobileCurrentCheckStatus?.loading && mobileCurrentCheckStatus?.available === false && mobileCurrentCheckStatus?.checkedValue === mobileInputValue && !errors[mobileInputName] && (
                      <span style={{ color: 'red', fontSize: '1.2em' }}>✗</span>
                    )}
                  </div>
                </InputGroup>
                {/* Display error specifically for the mobile number field */}
                {touched[mobileInputName] && errors[mobileInputName] && (
                  <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{t(errors[mobileInputName])}</div>
                )}
                {!errors[mobileInputName] && mobileCurrentCheckStatus?.error && (
                  <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{t(mobileCurrentCheckStatus.error)}</div>
                )}
              </Col>
            </Row>
          );

      case "country-code":
        return (
          <Input
            type="select"
            id={item.name}
            name={item.name}
            value={fieldValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            invalid={touched[item.name] && !!errors[item.name]}
          >
            {countriesList.map((c) => (
              <option key={c.ID} value={c.ID}>
                {`${c?.[i18n.language]} (${c.code})`}
              </option>
            ))}
          </Input>
        );

      case "user-type":
        return (
          <Input
            type="select"
            id={item.name}
            name={item.name}
            value={fieldValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            invalid={touched[item.name] && !!errors[item.name]}
          >
            <option value="">{t("SelectUserType")}</option>
            {userTypesList.map((type) => (
              <option key={type.ID} value={type.ID}>
                {i18n.language === "ar" ? type.UserTypeAr : type.UserTypeEn}
              </option>
            ))}
          </Input>
        );

      case "genders":
        return (
          <Input
            type="select"
            id={item.name}
            name={item.name}
            value={fieldValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            invalid={touched[item.name] && !!errors[item.name]}
          >
            <option value="">{t("SelectGender")}</option>
            {genders.map((g) => (
              <option key={g.ID} value={g.ID}>
                {g[i18n.language]}
              </option>
            ))}
          </Input>
        );

      case "courses":
        return (
          <Input
            type="select"
            id={item.name}
            name={item.name}
            value={fieldValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            invalid={touched[item.name] && !!errors[item.name]}
          >
            <option value="">{t("SelectCourse")}</option>
            {courses.map((course) => (
              <option key={course.ID} value={course.ID}>
                {i18n.language === "en" ? course.CourseTitleEn : course.CourseTitleAr}
              </option>
            ))}
          </Input>
        );

      case "Branches":
        const selectedBranchIds = Array.isArray(values[item.name]) ? values[item.name] : [];
        const selectedBranchObjects = branches.filter((branch) =>
            selectedBranchIds.includes(branch.BranchId)
        );

        return (
          <Multiselect
            options={branches}
            selectedValues={selectedBranchObjects}
            displayValue={i18n.language === "ar" ? "BranchNameAr" : "BranchNameEn"}
            onSelect={(selectedList, selectedItem) => {
              const newValues = selectedList.map((b) => b.BranchId);
              setFieldValue(item.name, newValues);
            }}
            onRemove={(selectedList, removedItem) => {
              const newValues = selectedList.map((b) => b.BranchId);
              setFieldValue(item.name, newValues);
            }}
            placeholder={t("SelectBranches")}
            className="form-control"
            style={{ multiselectContainer: { width: "100%" } }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Row>
      {labels.map((item) => {
        const isEmail = item.fieldName === "email-box";
        const isMobile = item.fieldName === "mobile-box";
        const currentCheckStatus = isEmail ? emailCheckStatus : (isMobile ? mobileCheckStatus : null);

        return (
          <Col sm={6} key={item.name}>
            <div className="mb-3">
              {/* For mobile-box, labels are handled inside its Row/Col structure */}
              {!isMobile && (
                <label htmlFor={item.name} className="col-form-label">
                  {t(item.labelName)}
                  {item.required && <span className="text-danger"> *</span>}
                </label>
              )} 
              {/* Render the field */}
              {renderField(item)}
              {/* Display error for the main field */}
              {!isMobile && touched[item.name] && errors[item.name] && (
                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{t(errors[item.name])}</div>
              )}
              {/* Display API check error if not already shown by Formik */}
              {isEmail && !errors[item.name] && currentCheckStatus?.error && (
                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{t(currentCheckStatus.error)}</div>
              )}
            </div>
          </Col>
        );
      })}
    </Row>
  );
};

export default AddUserForm;
